import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isDesktopUserAgent } from '../../utils/userAgent';

/**
 * Component to handle domain-based routing for sentry.trading
 * When users visit sentry.trading, route them to Sentry pages
 */
const SentryDomainRedirect: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if we're on the Sentry domain
    const isSentryDomain = 
      window.location.hostname === 'sentry.trading' || 
      window.location.hostname === 'www.sentry.trading';

    if (!isSentryDomain) {
      return; // Not on Sentry domain, no redirect needed
    }

    const path = location.pathname;
    const isStandalonePwa =
      window.matchMedia?.('(display-mode: standalone)')?.matches ||
      (window.navigator as any)?.standalone === true;
    const isDesktop = isDesktopUserAgent();

    if (isStandalonePwa) {
      if (isDesktop && !path.startsWith('/sentry-desktop')) {
        navigate('/sentry-desktop', { replace: true });
        return;
      }

      if (!isDesktop && !path.startsWith('/sentry-pwa')) {
        navigate('/sentry-pwa', { replace: true });
        return;
      }
    }

    if (!path.startsWith('/sentry-desktop') && !path.startsWith('/sentry-pwa')) {
      if (isDesktop) {
        if (path === '/dashboard' || path === '/dashboard/') {
          navigate('/sentry-desktop/dashboard', { replace: true });
          return;
        }
        if (path === '/tokens' || path === '/tokens/') {
          navigate('/sentry-desktop/tokens', { replace: true });
          return;
        }
        if (path === '/docs' || path.startsWith('/docs/')) {
          navigate(path.replace(/^\/docs/, '/sentry-desktop/docs'), { replace: true });
          return;
        }
      } else {
        if (path === '/dashboard' || path === '/dashboard/') {
          navigate('/sentry-pwa', { replace: true });
          return;
        }
        if (path === '/tokens' || path === '/tokens/') {
          navigate('/sentry-pwa/tokens', { replace: true });
          return;
        }
        if (path === '/docs' || path.startsWith('/docs/')) {
          navigate(path.replace(/^\/docs/, '/sentry-pwa/docs'), { replace: true });
          return;
        }
      }
    }

    // Handle root path - show landing page (no redirect needed, route handles it)
    if (path === '/') {
      return; // Landing page route will handle this
    }

    // Handle /dashboard, /docs, /tokens, /sentry-pwa, and /sentry-desktop - no redirect needed
    if (path === '/dashboard' || path === '/dashboard/' ||
        path === '/docs' || path.startsWith('/docs/') ||
        path === '/tokens' || path === '/tokens/' ||
        path === '/sentry-pwa' || path.startsWith('/sentry-pwa/') ||
        path === '/sentry-desktop' || path.startsWith('/sentry-desktop/')) {
      return; // Routes will handle these paths directly
    }

    // For any other path on sentry.trading (except root), redirect to dashboard
    if (path !== '/' && !path.startsWith('/Sentry') && !path.startsWith('/sentry') &&
        !path.startsWith('/dashboard') && !path.startsWith('/docs') &&
        !path.startsWith('/tokens') && !path.startsWith('/sentry-pwa') &&
        !path.startsWith('/sentry-desktop')) {
      navigate(isDesktop ? '/sentry-desktop' : '/sentry-pwa', { replace: true });
      return;
    }
  }, [location.pathname, navigate]);

  return <>{children}</>;
};

export default SentryDomainRedirect;
